import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Badge,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  ListItemIcon,
  ListItemText,
  Stack,
  alpha,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import {
  Menu as MenuIcon,
  Notifications,
  AccountCircle,
  Settings,
  Logout,
  Brightness4,
  Brightness7,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { toggleSidebar, toggleTheme } from '../../store/slices/uiSlice';
import { logoutUser } from '../../store/slices/authSlice';

const DRAWER_WIDTH = 280;

const PAGE_TITLES: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/customers': 'Customers',
  '/appointments': 'Appointments',
  '/services': 'Services',
  '/packages': 'Packages',
  '/staff': 'Staff',
  '/inventory': 'Inventory',
  '/finance': 'Finance',
  '/marketing': 'Marketing',
  '/analytics': 'Analytics',
  '/membership': 'Membership',
  '/inquiries': 'Inquiries',
  '/kiosks': 'Kiosks',
  '/settings': 'Settings',
  '/super-admin': 'Super Admin',
};

const pageTitleFor = (pathname: string): string => {
  for (const prefix of Object.keys(PAGE_TITLES)) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) return PAGE_TITLES[prefix];
  }
  return '';
};

const Header: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { sidebarOpen, theme, notifications } = useAppSelector((state) => state.ui);
  const { user } = useAppSelector((state) => state.auth);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null);
  const pageTitle = pageTitleFor(location.pathname);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  const handleLogout = () => {
    dispatch(logoutUser());
    handleMenuClose();
  };

  const handleToggleTheme = () => {
    dispatch(toggleTheme());
  };

  const unreadNotifications = notifications.filter(n => !n.timestamp).length;

  return (
    <AppBar position="fixed" sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}>
      <Toolbar sx={{ gap: 1 }}>
        <IconButton
          aria-label="toggle drawer"
          onClick={() => dispatch(toggleSidebar())}
          edge="start"
          size="small"
          sx={{ mr: 1 }}
        >
          <MenuIcon />
        </IconButton>

        <Typography
          variant="h5"
          noWrap
          component="h1"
          sx={{
            flexGrow: 1,
            fontSize: { xs: '1rem', sm: '1.125rem' },
            fontWeight: 700,
            color: 'text.primary',
            letterSpacing: '-0.01em',
          }}
        >
          {pageTitle}
        </Typography>

        <Stack direction="row" alignItems="center" spacing={{ xs: 0.5, sm: 1 }}>
          <IconButton size="small" onClick={handleToggleTheme} aria-label="toggle theme">
            {theme === 'dark' ? <Brightness7 fontSize="small" /> : <Brightness4 fontSize="small" />}
          </IconButton>

          <IconButton
            size="small"
            onClick={handleNotificationOpen}
            aria-label={`show ${unreadNotifications} new notifications`}
          >
            <Badge badgeContent={unreadNotifications} color="error" overlap="circular">
              <Notifications fontSize="small" />
            </Badge>
          </IconButton>

          <Box
            onClick={handleMenuOpen}
            role="button"
            aria-haspopup="true"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1,
              py: 0.5,
              ml: 0.5,
              borderRadius: 999,
              cursor: 'pointer',
              border: '1px solid',
              borderColor: 'divider',
              transition: 'background-color 120ms ease, border-color 120ms ease',
              '&:hover': {
                backgroundColor: (t) => alpha(t.palette.primary.main, 0.05),
                borderColor: 'primary.light',
              },
            }}
          >
            {user ? (
              <>
                <Avatar
                  sx={{
                    width: 30,
                    height: 30,
                    fontSize: '0.75rem',
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                  }}
                >
                  {user.firstName[0]}{user.lastName[0]}
                </Avatar>
                <Box sx={{ display: { xs: 'none', md: 'block' }, pr: 0.5 }}>
                  <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, lineHeight: 1.2, color: 'text.primary' }} noWrap>
                    {user.firstName} {user.lastName}
                  </Typography>
                  <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', lineHeight: 1.2 }} noWrap>
                    {user.role.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase())}
                  </Typography>
                </Box>
              </>
            ) : (
              <AccountCircle sx={{ color: 'text.secondary' }} />
            )}
          </Box>
        </Stack>

        {/* User Menu */}
        <Menu
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          {user && (
            <>
              <MenuItem disabled>
                <ListItemText
                  primary={`${user.firstName} ${user.lastName}`}
                  secondary={user.email}
                />
              </MenuItem>
              <Divider />
            </>
          )}
          <MenuItem onClick={handleMenuClose}>
            <ListItemIcon>
              <Settings fontSize="small" />
            </ListItemIcon>
            <ListItemText>Settings</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <Logout fontSize="small" />
            </ListItemIcon>
            <ListItemText>Logout</ListItemText>
          </MenuItem>
        </Menu>

        {/* Notifications Menu */}
        <Menu
          anchorEl={notificationAnchor}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={Boolean(notificationAnchor)}
          onClose={handleNotificationClose}
          PaperProps={{
            sx: { width: 320, maxHeight: 400 },
          }}
        >
          {notifications.length === 0 ? (
            <MenuItem disabled>
              <ListItemText primary="No notifications" />
            </MenuItem>
          ) : (
            notifications.slice(0, 5).map((notification) => (
              <MenuItem key={notification.id} onClick={handleNotificationClose}>
                <ListItemText
                  primary={notification.message}
                  secondary={new Date(notification.timestamp).toLocaleString()}
                />
              </MenuItem>
            ))
          )}
          {notifications.length > 5 && (
            <>
              <Divider />
              <MenuItem onClick={handleNotificationClose}>
                <ListItemText primary="View all notifications" />
              </MenuItem>
            </>
          )}
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default Header;